import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { safeGetItem, safeRemoveItem } from './services/safe-storage';

/**
 * Error interceptor — centralizes HTTP error handling across the application.
 *
 * - 401: Clears session and redirects to /login
 * - 403: Logs structured warning; individual components decide how to recover
 * - 5xx: Logs structured error for observability
 * - All: Transforms errors into a consistent format
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Sanitized log context — deliberately omits the URL and Angular's
      // default `message` (which embeds the full URL). Avoids leaking
      // backend topology / query params into the browser console where it
      // could be screenshotted, harvested by extensions, or shoulder-surfed.
      const errorContext = {
        status: error.status,
        timestamp: new Date().toISOString(),
      };

      switch (error.status) {
        case 0:
          console.error('[HTTP] Network error — server unreachable', errorContext);
          break;
        case 401:
          // Only treat as a session-expired redirect when the user
          // actually had a token. A 401 on an anonymous request to a
          // public endpoint (e.g. the home page's /api/home call)
          // should not bounce the visitor to /signup.
          if (safeGetItem('session', 'auth_token')) {
            safeRemoveItem('session', 'auth_token');
            router.navigate(['/login']);
          } else {
            console.warn('[HTTP] Unauthorized (anonymous)', errorContext);
          }
          break;
        case 403:
          console.warn('[HTTP] Forbidden', errorContext);
          break;
        case 404:
          // Let individual components handle 404s
          break;
        case 429:
          console.warn('[HTTP] Rate limited', errorContext);
          break;
        default:
          if (error.status >= 500) {
            console.error('[HTTP] Server error', errorContext);
          }
      }

      return throwError(() => error);
    }),
  );
};
